import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import type { Map } from "mapbox-gl";
import type { PlaceFeature, PlacePreviewFeature } from "~/lib/api";
import { getMapboxConfig } from "~/lib/config";
import { useInstrumentation, type SearchSuggestionType } from "~/hooks/useInstrumentation";
import { cn } from "~/lib/utils";
import { Icon } from "~/components/ui/icons/icon";
import { useMapSearch, type SearchSuggestion } from "./useMapSearch";
import { SearchResultList } from "./SearchResultList";
import { SearchResultListItem } from "./SearchResultListItem";
import { placeFeatureToItem, suggestionToItem } from "./utils";
import type { SearchResultItem } from "./types";

/** Unified dropdown item for rendering */
type DropdownItem =
  | { type: "suggestion"; suggestion: SearchSuggestion; item: SearchResultItem }
  | { type: "result"; result: PlacePreviewFeature; item: SearchResultItem };

interface MapSearchBoxProps {
  map: Map | null;
  className?: string;
  /** Existing places to include in search (adventure map + noma.to) */
  places?: PlaceFeature[];
  /** Called when search results change (for consumer rendering markers) */
  onSearchResults?: (results: PlacePreviewFeature[]) => void;
  /** Called when a result is selected. Options indicate how the selection was made. */
  onFeatureSelect?: (feature: PlacePreviewFeature, options?: { fromKeyboard?: boolean }) => void;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface MapSearchBoxRef {
  clear: () => void;
}

function suggestionToDropdownItem(suggestion: SearchSuggestion): SearchResultItem {
  switch (suggestion.type) {
    case "local":
      return placeFeatureToItem(suggestion.place);
    case "mapbox":
      return suggestionToItem(suggestion.suggestion);
    case "coordinate":
      return {
        id: `coords-${suggestion.coords.join(",")}`,
        name: `${suggestion.coords[1].toFixed(5)}, ${suggestion.coords[0].toFixed(5)}`,
        subtitle: "Go to coordinates",
        placeType: null,
      };
  }
}

function getSuggestionType(suggestion: SearchSuggestion): SearchSuggestionType {
  if (suggestion.type === "local") return "place";
  if (suggestion.type === "coordinate") return "coordinates";

  const featureType = suggestion.suggestion.feature_type;
  if (featureType === "category") return "category";
  if (featureType === "brand") return "brand";
  return "place";
}

function resultToDropdownItem(result: PlacePreviewFeature): SearchResultItem {
  const coords = result.geometry.coordinates.join(",");
  const fallbackId = `${coords}-${result.properties.name}-${result.properties.type ?? "unknown"}`;
  return {
    id: result.properties.id || fallbackId,
    name: result.properties.name,
    subtitle: result.properties.description ?? "",
    placeType: result.properties.type,
  };
}

/**
 * Map search box that searches both Mapbox POIs and custom places.
 * Custom places are prioritized and deduplicated against Mapbox results.
 *
 * @example
 * ```tsx
 * const searchRef = useRef<MapSearchBoxRef>(null);
 *
 * <MapSearchBox
 *   ref={searchRef}
 *   map={map}
 *   places={myPlaces}
 *   onSearchResults={setMarkers}
 *   onFeatureSelect={handleSelect}
 * />
 *
 * // Clear search programmatically
 * searchRef.current?.clear();
 * ```
 */
export const MapSearchBox = forwardRef<MapSearchBoxRef, MapSearchBoxProps>(function MapSearchBox(
  { map, className, places = [], onSearchResults, onFeatureSelect, onOpenChange },
  ref,
) {
  const mapboxConfig = getMapboxConfig();
  const instrumentation = useInstrumentation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const autoSelectedResultRef = useRef<PlacePreviewFeature | null>(null);
  // Track keyboard-initiated suggestion selections for auto-select
  const keyboardSelectionRef = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [announcement, setAnnouncement] = useState("");

  const { query, suggestions, results, isLoading, search, selectSuggestion, clear } = useMapSearch({
    accessToken: mapboxConfig.token,
    map,
    places,
  });

  const handleClear = useCallback(() => {
    clear();
    setIsOpen(false);
    setActiveIndex(-1);
    autoSelectedResultRef.current = null;
    keyboardSelectionRef.current = false;
  }, [clear]);

  useImperativeHandle(ref, () => ({ clear: handleClear }), [handleClear]);

  // Are we showing results (Step 2) or suggestions (Step 1)?
  const hasResults = results.length > 0;

  // Build dropdown items from either suggestions or results
  const dropdownItems = useMemo<DropdownItem[]>(() => {
    if (hasResults) {
      return results.map((result) => ({
        type: "result" as const,
        result,
        item: resultToDropdownItem(result),
      }));
    }
    return suggestions.map((suggestion) => ({
      type: "suggestion" as const,
      suggestion,
      item: suggestionToDropdownItem(suggestion),
    }));
  }, [hasResults, results, suggestions]);

  // Notify consumer when results change
  useEffect(() => {
    onSearchResults?.(results);
  }, [results, onSearchResults]);

  // Auto-select single results
  // - isOpen: only during active search, not on initial load/back navigation
  // - ref: prevent re-selecting same result when isOpen toggles (e.g., focus)
  useEffect(() => {
    if (results.length === 1 && isOpen && results[0] !== autoSelectedResultRef.current) {
      autoSelectedResultRef.current = results[0];

      const fromKeyboard = keyboardSelectionRef.current;
      keyboardSelectionRef.current = false;

      if (fromKeyboard) inputRef.current?.blur();
      onFeatureSelect?.(results[0], { fromKeyboard });

      setIsOpen(false);
      setAnnouncement(`Selected ${results[0].properties.name}`);
    }
  }, [results, onFeatureSelect, isOpen]);

  const selectItem = useCallback(
    async (item: DropdownItem, options?: { fromKeyboard?: boolean }) => {
      if (item.type === "suggestion") {
        const { suggestion } = item;
        // Track keyboard selection for auto-select effect
        keyboardSelectionRef.current = !!options?.fromKeyboard;
        instrumentation.track("Search Suggestion Selected", {
          suggestionType: getSuggestionType(suggestion),
          suggestionSource: suggestion.type === "mapbox" ? "mapbox" : "noma",
        });
        await selectSuggestion(suggestion);
      } else {
        onFeatureSelect?.(item.result, options);
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [selectSuggestion, onFeatureSelect, instrumentation],
  );

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent) => {
      const itemCount = dropdownItems.length;

      const navigate = (getNext: (prev: number) => number) => {
        setActiveIndex((prev) => {
          const next = getNext(prev);
          // Preview on keyboard navigation (fly to location)
          const item = dropdownItems[next];
          if (item?.type === "result" && map) {
            const coords = item.result.geometry.coordinates as [number, number];
            map.flyTo({ center: coords, zoom: Math.max(map.getZoom(), 12), duration: 500 });
          }
          return next;
        });
      };

      switch (e.key) {
        case "ArrowDown":
          if (!isOpen || itemCount === 0) return;
          e.preventDefault();
          navigate((prev) => (prev < itemCount - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          if (!isOpen || itemCount === 0) return;
          e.preventDefault();
          navigate((prev) => (prev > 0 ? prev - 1 : itemCount - 1));
          break;
        case "Tab":
          if (!isOpen || itemCount === 0) return;
          e.preventDefault();
          navigate(
            e.shiftKey
              ? (prev) => (prev > 0 ? prev - 1 : itemCount - 1)
              : (prev) => (prev < itemCount - 1 ? prev + 1 : 0),
          );
          break;
        case "Enter":
          e.preventDefault();
          if (itemCount > 0) {
            const index = activeIndex >= 0 ? activeIndex : 0;
            await selectItem(dropdownItems[index], { fromKeyboard: true });
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    },
    [isOpen, dropdownItems, activeIndex, selectItem, map],
  );

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listboxRef.current) {
      const activeElement = listboxRef.current.children[activeIndex] as HTMLElement;
      activeElement?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!inputRef.current?.contains(target) && !listboxRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Ctrl+F to focus search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  const showDropdown = isOpen && dropdownItems.length > 0;

  useEffect(() => {
    onOpenChange?.(showDropdown);
  }, [showDropdown, onOpenChange]);

  // Open dropdown when we have suggestions to show (not after selecting a result)
  useEffect(() => {
    if (suggestions.length > 0 && query.trim() && results.length === 0) {
      setIsOpen(true);
      setActiveIndex(-1);
    }
  }, [suggestions.length, query, results.length]);

  if (!mapboxConfig.token) return null;

  const listboxId = "map-search-listbox";

  return (
    <div className={cn("relative", className)}>
      {/* Screen reader announcement for auto-select */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={activeIndex >= 0 ? `search-option-${activeIndex}` : undefined}
          aria-autocomplete="list"
          aria-label="Search for a place"
          value={query}
          onChange={(e) => search(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => dropdownItems.length > 0 && setIsOpen(true)}
          placeholder="Search or paste coordinates..."
          className="w-full rounded-xl border border-border bg-background py-2.5 pl-4 pr-10 text-sm shadow-lg outline-none transition-shadow placeholder:text-muted-foreground focus:ring-2 focus:ring-ring/50"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isLoading ? (
            <Icon i="loader" className="size-5 animate-spin text-muted-foreground" />
          ) : hasResults ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <Icon i="search-clear" className="size-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                inputRef.current?.focus();
                setIsOpen(true);
              }}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Search"
            >
              <Icon i="search" className="size-5" />
            </button>
          )}
        </div>
      </div>

      {showDropdown && (
        <SearchResultList ref={listboxRef} id={listboxId}>
          {dropdownItems.map((dropdownItem, index) => (
            <SearchResultListItem
              key={dropdownItem.item.id}
              item={dropdownItem.item}
              index={index}
              isActive={index === activeIndex}
              onClick={() => selectItem(dropdownItem)}
              onMouseEnter={() => setActiveIndex(index)}
            />
          ))}
        </SearchResultList>
      )}
    </div>
  );
});
